<script setup lang="ts">
import { ChoiceField } from '@haskoning/vue-component-library'
import { useTransportConfiguration } from '@/components/calculation-form/transport-configuration/composable/useTransportConfiguration'
import type { CalculationFormMixinProps } from '@/components/calculation-form/type/type'
import { handleWheelOnNumericField } from '@/utils/handleWheelOnNumericField'

const props = defineProps<CalculationFormMixinProps>()

const {
  isReadOnly,
  transportConfigStore,
  newConfig,
  existingConfigs,
  availableBuildingBlocks,
  availableDikeSections,
  transportNoticeTitle,
  transportMissingMessages,
  handleLoading,
  addTransportConfig,
  debouncedStoreUpdateDynamic,
  deleteConfig,
  handleNewBuildingBlockChange,
  handleCodeChange,
  handleExistingDistanceChange,
  handleExistingBuildingBlockChange,
  handleExistingCodeChange,
  getBuildingBlockValue,
  getCodeValue
} = useTransportConfiguration(props)
</script>

<template>
  <template v-if="handleLoading()">
    <div class="d-flex justify-center align-center fill-height pa-6">
      <v-progress-circular indeterminate color="primary" size="50"></v-progress-circular>
    </div>
  </template>
  <template v-else>
    <v-container fluid class="pa-6">
      <v-row class="mb-4">
        <v-col cols="12">
          <h1 class="text-h4 mr-4">Transport Configuration</h1>
        </v-col>
        <v-col>
          <p class="text-body-2 text-medium-emphasis mb-0">
            The environmental impact of equipment replacing materials depends on transport distance.
            Here, you can define distances for specific equipment within customized building blocks.
            The entered distance is multiplied by fuel consumption per kilometer, as specified in
            transport phases (A2, A4, or C2) of your customized profiles.
          </p>
        </v-col>
        <v-col v-if="transportMissingMessages.length" cols="12" class="pt-0">
          <v-alert type="warning" variant="tonal" density="comfortable" class="mb-0">
            <div class="text-body-2">
              {{ transportNoticeTitle }}
            </div>
            <div
              v-for="(message, index) in transportMissingMessages"
              :key="`transport-switch-missing-${index}`"
              class="text-body-2 mt-1"
            >
              {{ message }}
            </div>
          </v-alert>
        </v-col>
      </v-row>
      <div>
        <!-- Add New Transport Config Section -->
        <v-col cols="12">
          <h3 class="text-h6 mb-2">Add New Transport Distance Configuration</h3>
        </v-col>
        <v-row v-if="!isReadOnly" class="ml-0 mr-0 align-center mb-3 pa-3 rounded">
          <v-col cols="12" sm="6" md="3" lg="3" class="pa-2">
            <v-select
              :model-value="newConfig.building_block"
              :items="availableBuildingBlocks"
              item-title="title"
              item-value="value"
              label="Building Block"
              hide-details
              variant="filled"
              color="primary"
              @update:model-value="handleNewBuildingBlockChange"
            />
          </v-col>
          <v-col cols="12" sm="6" md="2" lg="2" class="pa-2">
            <ChoiceField
              v-if="transportConfigStore.fields?.stage"
              :field="transportConfigStore.fields.stage"
              :model-value="newConfig"
              hide-details
              class="text-truncate"
              color="primary"
            />
          </v-col>
          <v-col cols="12" sm="6" md="2" lg="2" class="pa-2">
            <ChoiceField
              v-if="transportConfigStore.fields?.transport_category"
              :field="transportConfigStore.fields.transport_category"
              :model-value="newConfig"
              hide-details
              class="text-truncate"
              color="primary"
            />
          </v-col>
          <v-col cols="12" sm="6" md="2" lg="2" class="pa-2">
            <v-select
              :model-value="newConfig.code"
              :items="availableDikeSections"
              item-title="title"
              item-value="value"
              label="Dike Section"
              color="primary"
              hide-details
              variant="filled"
              @update:model-value="handleCodeChange"
            />
          </v-col>
          <v-col cols="12" sm="3" md="2" lg="2" class="pa-2">
            <v-text-field
              v-model.number="newConfig.distance"
              label="Distance (km)"
              type="number"
              color="primary"
              hide-details
              variant="filled"
              @wheel="handleWheelOnNumericField"
            />
          </v-col>
          <v-col cols="12" sm="1" md="1" lg="1" class="pa-2">
            <v-btn
              color="primary"
              @click="addTransportConfig"
              :disabled="
                (newConfig.building_block == null && !newConfig.apply_to_all_building_block) ||
                !newConfig.stage ||
                (newConfig.code == null && !newConfig.apply_to_all_code)
              "
            >
              Add
            </v-btn>
          </v-col>
        </v-row>

        <v-divider class="my-4" />

        <!-- Existing Transport Configs List -->
        <v-row class="ml-0 mr-0">
          <v-col cols="12">
            <h3 class="text-h6 mb-4">Existing Transport Configurations</h3>
          </v-col>
        </v-row>

        <div v-if="existingConfigs.length === 0" class="text-center py-8 text-medium-emphasis">
          No transport configurations yet.
        </div>

        <v-row
          v-for="config in existingConfigs"
          :key="config.id"
          class="ml-0 mr-0 align-center mb-3 pa-3 rounded"
        >
          <v-col cols="12" sm="6" md="3" lg="3" class="pa-2">
            <v-select
              :model-value="getBuildingBlockValue(config)"
              :items="availableBuildingBlocks"
              item-title="title"
              item-value="value"
              label="Building Block"
              hide-details
              variant="filled"
              color="primary"
              :readonly="isReadOnly"
              @update:model-value="
                (selectedValue) => handleExistingBuildingBlockChange(config, selectedValue)
              "
            />
          </v-col>
          <v-col cols="12" sm="6" md="2" lg="2" class="pa-2">
            <ChoiceField
              v-if="transportConfigStore.fields?.stage"
              :field="transportConfigStore.fields.stage"
              :model-value="config"
              hide-details
              :readonly="isReadOnly"
              @update="debouncedStoreUpdateDynamic(config)"
              class="text-truncate"
              color="primary"
            />
          </v-col>
          <v-col cols="12" sm="6" md="2" lg="2" class="pa-2">
            <ChoiceField
              v-if="transportConfigStore.fields?.transport_category"
              :field="transportConfigStore.fields.transport_category"
              :model-value="config"
              hide-details
              :readonly="isReadOnly"
              @update="debouncedStoreUpdateDynamic(config)"
              class="text-truncate"
              color="primary"
            />
          </v-col>
          <v-col cols="12" sm="6" md="2" lg="2" class="pa-2">
            <v-select
              :model-value="getCodeValue(config)"
              :items="availableDikeSections"
              item-title="title"
              item-value="value"
              label="Dike Section"
              hide-details
              variant="filled"
              :readonly="isReadOnly"
              color="primary"
              @update:model-value="
                (selectedValue) => handleExistingCodeChange(config, selectedValue)
              "
            />
          </v-col>
          <v-col cols="12" sm="3" md="2" lg="2" class="pa-2">
            <v-text-field
              :model-value="config.distance"
              label="Distance (km)"
              type="number"
              min="0"
              hide-details
              variant="filled"
              :readonly="isReadOnly"
              color="primary"
              @wheel="handleWheelOnNumericField"
              @blur="() => handleExistingDistanceChange(config, config.distance)"
            />
          </v-col>
          <v-col cols="12" sm="1" md="1" lg="1" class="pa-2 d-flex">
            <v-btn
              icon="mdi-delete"
              variant="text"
              :disabled="isReadOnly"
              @click="deleteConfig(config)"
            />
          </v-col>
        </v-row>
      </div>
    </v-container>
  </template>
</template>
